import {
  NAMESPACES,
  nodeKeyRank,
  nodeTypeRank,
  type ContextTermValue,
  type JsonLdContext,
} from '../vocabulary/index.js';

/** Byte-stable string comparison. Deliberately not locale-aware. */
export function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

interface SortableNode {
  readonly '@id'?: unknown;
  readonly '@type'?: unknown;
  readonly order?: unknown;
}

function idOf(node: SortableNode): string {
  return typeof node['@id'] === 'string' ? node['@id'] : '';
}

function typeOf(node: SortableNode): string {
  return typeof node['@type'] === 'string' ? node['@type'] : '';
}

function orderOf(node: SortableNode): number {
  return typeof node.order === 'number' ? node.order : Number.MAX_SAFE_INTEGER;
}

/**
 * Canonical node sequence: type group, then `order`, then `@id`. Applied by both
 * the parser and the serialiser, which is what makes export byte-stable and
 * `parse(serialise(g))` a fixed point — `@graph` is a set in JSON-LD, so
 * reordering it changes no meaning.
 */
export function sortNodes<T extends SortableNode>(nodes: readonly T[]): T[] {
  return [...nodes].sort((a, b) => {
    const byType = nodeTypeRank(typeOf(a)) - nodeTypeRank(typeOf(b));
    if (byType !== 0) return byType;
    const byOrder = orderOf(a) - orderOf(b);
    if (byOrder !== 0) return byOrder;
    return compareStrings(idOf(a), idOf(b));
  });
}

/** Canonical key sequence inside a node: `@id`, `@type`, name, description, A→Z. */
export function orderNodeKeys(node: object): Record<string, unknown> {
  // Cast rather than declare an index signature: callers pass schema-inferred
  // node types, which have known keys and no index signature of their own.
  const source = node as Record<string, unknown>;
  const keys = Object.keys(source).sort((a, b) => {
    const byPriority = nodeKeyRank(a) - nodeKeyRank(b);
    return byPriority !== 0 ? byPriority : compareStrings(a, b);
  });
  const ordered: Record<string, unknown> = {};
  for (const key of keys) ordered[key] = source[key];
  return ordered;
}

const CONTEXT_KEY_PRIORITY: readonly string[] = [
  ...Object.keys(NAMESPACES),
  'name',
  'description',
];

/** Canonical key sequence inside `@context`: namespaces, borrowed terms, A→Z. */
export function orderContext(context: JsonLdContext): Record<string, ContextTermValue> {
  const keys = Object.keys(context).sort((a, b) => {
    const rankA = CONTEXT_KEY_PRIORITY.indexOf(a);
    const rankB = CONTEXT_KEY_PRIORITY.indexOf(b);
    const normalisedA = rankA < 0 ? CONTEXT_KEY_PRIORITY.length : rankA;
    const normalisedB = rankB < 0 ? CONTEXT_KEY_PRIORITY.length : rankB;
    return normalisedA !== normalisedB ? normalisedA - normalisedB : compareStrings(a, b);
  });
  const ordered: Record<string, ContextTermValue> = {};
  for (const key of keys) {
    const value = context[key];
    if (value !== undefined) ordered[key] = value;
  }
  return ordered;
}
